/**
 * /:wsId — workspace home screen. Shows KPI tiles, a project grid, and
 * "create project" CTA. Empty state when no projects exist.
 */

import { useState } from 'react'
import { useParams, Link } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'
import { useWorkspaceOverview, useWorkspaceMembers } from '@/features/workspaces/workspaces'
import { useCreateProject } from '@/features/projects/board'
import { useRealtime } from '@/lib/ws'
import { invalidateAfterTransfer } from '@/features/transfers/transfers'
import { CreateProjectDialog } from '@/components/workspace/CreateProjectDialog'
import { toast } from '@/features/feedback/toast'
import { ApiError } from '@/lib/api'
import { cn } from '@/lib/utils'
import type { ProjectWithStats } from '@friday/shared/realtime'

export function WorkspaceOverview() {
  const { wsId } = useParams({ strict: false }) as { wsId: string }
  const { data, isLoading, error } = useWorkspaceOverview(wsId)
  const createProject = useCreateProject(wsId)
  const membersQ = useWorkspaceMembers(wsId)
  const canShare = membersQ.data?.some((m) => m.role === 'owner' || m.role === 'admin') ?? false
  const [creating, setCreating] = useState(false)

  // Subscribe to realtime so the overview refreshes when tasks change.
  useRealtimeInvalidate(wsId)

  if (isLoading) {
    return (
      <div className="text-surface-500 flex h-full items-center justify-center text-sm">
        Loading workspace…
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-red-500">
        Failed to load workspace overview.
      </div>
    )
  }

  const { workspace, projects, totals } = data
  const ws = workspace

  return (
    <div className="h-full overflow-auto">
      <header
        data-masthead
        className="border-surface-200 dark:border-surface-800 dark:bg-surface-900/60 border-b bg-white/60 px-6 py-4 backdrop-blur md:pr-14"
      >
        <div className="flex flex-wrap items-center gap-3">
          <span className="bg-brand-500 flex h-9 w-9 items-center justify-center rounded-md text-sm font-semibold text-white">
            {(ws.slug || ws.name || 'WS').slice(0, 2).toUpperCase()}
          </span>
          <div className="min-w-0 flex-1 basis-1/2 md:basis-auto">
            <h1 className="truncate text-lg font-semibold">{ws.name}</h1>
            <div className="text-surface-500 text-xs">Workspace overview</div>
          </div>
          <button onClick={() => setCreating(true)} className="btn-primary text-sm">
            + New project
          </button>
          <WorkspaceHeaderLinks wsId={wsId} canShare={canShare} />
        </div>
      </header>

      <div className="space-y-6 p-6">
        <KpiRow totals={totals} />

        {projects.length === 0 ? (
          <EmptyState onCreate={() => setCreating(true)} wsName={ws.name} />
        ) : (
          <ProjectGrid projects={projects} />
        )}
      </div>

      {creating && (
        <CreateProjectDialog
          onClose={() => setCreating(false)}
          busy={createProject.isPending}
          onSubmit={async (input) => {
            try {
              const p = await createProject.mutateAsync(input)
              setCreating(false)
              toast(`Project "${p.name}" created`, { kind: 'success' })
            } catch (e) {
              const msg = e instanceof ApiError ? e.message : 'Failed to create project'
              toast(msg, { kind: 'error' })
            }
          }}
        />
      )}
    </div>
  )
}

/**
 * Workspace header links. Exported so the responsive header can be exercised
 * in a browser test without booting the whole overview screen.
 */
export function WorkspaceHeaderLinks({ wsId, canShare }: { wsId: string; canShare: boolean }) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Link
        to="/w/$wsId/share"
        params={{ wsId }}
        className="btn-ghost text-sm"
        title={
          canShare
            ? 'Share this workspace with teammates'
            : 'Only workspace owners and admins can share the workspace'
        }
      >
        Share workspace
      </Link>
      <Link to="/w/$wsId/settings" params={{ wsId }} className="btn-ghost text-sm" title="Workspace settings">
        Settings
      </Link>
    </div>
  )
}

function KpiRow({
  totals,
}: {
  totals: { projects: number; tasks: number; open: number; inProgress: number; done: number }
}) {
  const tiles: Array<{ label: string; value: number; tone?: string }> = [
    { label: 'Projects', value: totals.projects },
    { label: 'Tasks', value: totals.tasks },
    { label: 'In progress', value: totals.inProgress, tone: 'text-amber-700 dark:text-amber-300' },
    { label: 'Done', value: totals.done, tone: 'text-green-700 dark:text-green-300' },
  ]
  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
      {tiles.map((t) => (
        <div key={t.label} className="card p-4">
          <div className="text-surface-500 text-[11px] font-semibold uppercase tracking-wider">{t.label}</div>
          <div className={cn('mt-1 text-2xl font-semibold tabular-nums', t.tone)}>{t.value}</div>
        </div>
      ))}
    </div>
  )
}

function ProjectGrid({ projects }: { projects: ProjectWithStats[] }) {
  return (
    <div>
      <h2 className="text-surface-500 mb-3 text-xs font-semibold uppercase tracking-wider">Projects</h2>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        {projects.map((p) => (
          <Link
            key={p.id}
            to="/w/$wsId/p/$pKey"
            params={{ wsId: p.workspaceId, pKey: p.key }}
            className="card hover:border-brand-500 group block p-4 transition-colors"
          >
            <div className="flex items-start gap-3">
              <span
                className="flex h-9 w-9 items-center justify-center rounded-md text-sm font-semibold text-white"
                style={{ backgroundColor: p.color }}
              >
                {p.key.slice(0, 2)}
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex items-baseline gap-2">
                  <h3 className="group-hover:text-brand-600 dark:group-hover:text-brand-300 truncate text-sm font-semibold">
                    {p.name}
                  </h3>
                  <span className="text-surface-500 font-mono text-[10px]">{p.key}</span>
                </div>
                {p.description ? (
                  <p className="text-surface-500 mt-0.5 line-clamp-2 text-xs">{p.description}</p>
                ) : (
                  <p className="text-surface-400 mt-0.5 text-xs">No description</p>
                )}
              </div>
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-1.5 text-[10px]">
              <StatusPill label="Open" value={p.taskCounts.open} tone="bg-surface-200 text-surface-700" />
              <StatusPill
                label="In progress"
                value={p.taskCounts.in_progress}
                tone="bg-amber-500/15 text-amber-700 dark:text-amber-300"
              />
              <StatusPill
                label="In review"
                value={p.taskCounts.in_review}
                tone="bg-violet-500/15 text-violet-700 dark:text-violet-300"
              />
              <StatusPill
                label="Done"
                value={p.taskCounts.done}
                tone="bg-green-500/15 text-green-700 dark:text-green-300"
              />
            </div>
            <div className="text-surface-500 mt-2 text-[10px]">
              {p.lastActivityAt ? `Last activity ${formatRelative(p.lastActivityAt)}` : 'No activity yet'}
            </div>
          </Link>
        ))}
      </div>
    </div>
  )
}

function StatusPill({ label, value, tone }: { label: string; value: number; tone: string }) {
  return (
    <span
      data-status-pill
      className={cn('inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-medium', tone)}
    >
      <span className="font-mono tabular-nums">{value}</span>
      <span>{label}</span>
    </span>
  )
}

function EmptyState({ onCreate, wsName }: { onCreate: () => void; wsName: string }) {
  return (
    <div className="card p-12 text-center">
      <div className="text-2xl">📁</div>
      <h2 className="mt-2 text-base font-semibold">No projects in {wsName} yet</h2>
      <p className="text-surface-500 mt-1 text-sm">
        Projects hold tasks, columns, and sprints. Create your first one to get started.
      </p>
      <button onClick={onCreate} className="btn-primary mt-4">
        + New project
      </button>
    </div>
  )
}

function formatRelative(iso: string): string {
  const d = new Date(iso)
  const diff = Date.now() - d.getTime()
  const min = Math.floor(diff / 60_000)
  if (min < 1) return 'just now'
  if (min < 60) return `${min}m ago`
  const hr = Math.floor(min / 60)
  if (hr < 24) return `${hr}h ago`
  const days = Math.floor(hr / 24)
  if (days < 30) return `${days}d ago`
  return d.toLocaleDateString()
}

/** Subscribe to the workspace's realtime stream so the overview refreshes
 * whenever any project inside this workspace changes. */
function useRealtimeInvalidate(wsId: string) {
  const qc = useQueryClient()
  useRealtime({
    wsId,
    subscribe: [{ kind: 'workspace', id: wsId }],
    onEvent: (e) => {
      if (
        e.t === 'task.updated' ||
        e.t === 'task.moved' ||
        e.t === 'task.created' ||
        e.t === 'task.archived' ||
        e.t === 'task.restored' ||
        e.t === 'task.deleted' ||
        e.t === 'project.updated' ||
        e.t === 'project.archived' ||
        e.t === 'project.created' ||
        e.t === 'transfer.completed'
      ) {
        void qc.invalidateQueries({ queryKey: ['workspace-overview', wsId] })
        if (e.t === 'transfer.completed') invalidateAfterTransfer(qc, [wsId])
      }
    },
  })
}
